import * as _ from 'lodash-es';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

// TODO: This file should be refactored so that we don't need these complex fragments anymore.
// Right now, we can't easily track field usage through the type system in 'slab-graphql/no-unused-fields'.
// eslint-disable-next-line slab-graphql/no-unused-fields
gql(`
  # eslint-disable-next-line @graphql-eslint/require-selections
  fragment getTopicUsers_topic_base on Topic {
    owners {
      id
    }
    ownerGroups {
      id
      allUsers {
        id
      }
    }
    members {
      id
    }
    memberGroups {
      id
      allUsers {
        id
      }
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
const GET_TOPIC_USERS_TOPIC_FRAGMENT = gql(`
  fragment getTopicUsers_topic on Topic {
    id
    ...getTopicUsers_topic_base
    inheritedAncestors {
      id
      ...getTopicUsers_topic_base
    }
  }
`);

type BaseGroup<User> = { allUsers?: User[] };
type NonInheritedOwnersTopic<User> = {
  owners: User[];
  ownerGroups: BaseGroup<User>[];
};
type NonInheritedMembersTopic<User> = {
  members: User[];
  memberGroups: BaseGroup<User>[];
};
type NonInheritedUsersTopic<User> = NonInheritedMembersTopic<User> &
  NonInheritedOwnersTopic<User>;

const getGroupsUsers = <User>(groups?: BaseGroup<User>[] | null) =>
  (groups || []).flatMap((t) => t?.allUsers || []);

const getGroupOwners = _.memoize(
  <User>({ ownerGroups }: { ownerGroups: BaseGroup<User>[] }) =>
    getGroupsUsers(ownerGroups),
);

const getGroupMembers = _.memoize(
  <User>({ memberGroups }: { memberGroups: BaseGroup<User>[] }) =>
    getGroupsUsers(memberGroups),
);

export const getDirectOwners = _.memoize(
  <User>({ owners }: { owners: User[] }) => owners,
);

export const getDirectMembers = _.memoize(
  <User>({ members }: { members: User[] }) => members,
);

const getNonInheritedOwners = _.memoize(
  <User>(topic: NonInheritedOwnersTopic<User>) => [
    ...(getDirectOwners(topic) || []),
    ...getGroupOwners(topic),
  ],
);

const getNonInheritedMembers = _.memoize(
  <User>(topic: NonInheritedMembersTopic<User>) => [
    ...(getDirectMembers(topic) || []),
    ...getGroupMembers(topic),
  ],
);

const getInheritedOwners = <User>({
  inheritedAncestors,
}: {
  inheritedAncestors?: NonInheritedOwnersTopic<User>[] | null;
}) => (inheritedAncestors || []).flatMap((t) => t && getNonInheritedOwners(t));

const getInheritedMembers = <User>({
  inheritedAncestors,
}: {
  inheritedAncestors?: NonInheritedMembersTopic<User>[] | null;
}) => (inheritedAncestors || []).flatMap((t) => t && getNonInheritedMembers(t));

export const getInheritedUsers = _.memoize(
  <User>(
    topic: DocumentType<typeof GET_INHERITED_USERS_TOPIC_FRAGMENT> & {
      inheritedAncestors?: NonInheritedUsersTopic<User>[] | null;
    },
  ) => {
    const inheritedOwners = getInheritedOwners(topic);
    const inheritedMembers = _.differenceBy(
      getInheritedMembers(topic),
      inheritedOwners,
      'id',
    );

    return {
      owners: _.uniqBy(inheritedOwners, 'id'),
      members: _.uniqBy(inheritedMembers, 'id'),
    };
  },
);

const GET_INHERITED_USERS_TOPIC_FRAGMENT = gql(`
  fragment getInheritedUsers_topic on Topic {
    ...getTopicUsers_topic
  }
`);

const getTopicUsers = _.memoize(
  <User>(
    topic: DocumentType<typeof GET_TOPIC_USERS_TOPIC_FRAGMENT> &
      NonInheritedUsersTopic<User> & {
        inheritedAncestors?: NonInheritedUsersTopic<User>[] | null;
      },
  ) => {
    const directOwners = getNonInheritedOwners(topic);
    const inheritedOwners = getInheritedOwners(topic);
    const owners = _.uniqBy([...directOwners, ...inheritedOwners], 'id');

    const directMembers = getNonInheritedMembers(topic);
    const inheritedMembers = getInheritedMembers(topic);
    const members = _.differenceBy(
      _.uniqBy([...directMembers, ...inheritedMembers], 'id'),
      owners,
      'id',
    );

    return { owners, members };
  },
);

export default getTopicUsers;

const GET_TOPIC_USER_ID_SET_TOPIC_FRAGMENT = gql(`
  fragment getTopicUserIdSet_topic on Topic {
    ...getTopicUsers_topic
  }
`);

export const getTopicUserIdSet = _.memoize(
  (topic: DocumentType<typeof GET_TOPIC_USER_ID_SET_TOPIC_FRAGMENT>) => {
    const directOwners = getNonInheritedOwners(topic);
    const inheritedOwners = getInheritedOwners(topic);
    const owners = new Set(_.map([...directOwners, ...inheritedOwners], 'id'));

    const directMembers = getNonInheritedMembers(topic);
    const inheritedMembers = getInheritedMembers(topic);
    const members = new Set(
      _.map([...directMembers, ...inheritedMembers], 'id'),
    );

    owners.forEach((id) => members.delete(id));

    return { owners, members };
  },
);

export enum TopicAccessSource {
  Direct = 'DIRECT',
  Inherited = 'INHERITED',
  Group = 'GROUP',
}

type TopicAccessSourceMap = Record<TopicAccessSource, boolean>;

export interface UserTopicAccessSource {
  owner: TopicAccessSourceMap;
  member: TopicAccessSourceMap;
}

export const getTopicAccessSource = _.memoize(
  (
    topic: DocumentType<typeof GET_TOPIC_USER_ID_SET_TOPIC_FRAGMENT>,
    userId: string,
  ): UserTopicAccessSource => {
    const groupOwners = getGroupOwners(topic);
    const groupMembers = getGroupMembers(topic);

    const directOwners = getDirectOwners(topic);
    const directMembers = getDirectMembers(topic);

    const inheritedOwners = getInheritedOwners(topic);
    const inheritedMembers = getInheritedMembers(topic);

    const match = (arr: { id: string }[]) => _.some(arr, { id: userId });

    return {
      owner: {
        [TopicAccessSource.Direct]: match(directOwners),
        [TopicAccessSource.Inherited]: match(inheritedOwners),
        [TopicAccessSource.Group]: match(groupOwners),
      },
      member: {
        [TopicAccessSource.Direct]: match(directMembers),
        [TopicAccessSource.Inherited]: match(inheritedMembers),
        [TopicAccessSource.Group]: match(groupMembers),
      },
    };
  },
);
